import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { wsManuscriptEventUrl } from "utils/urls";

export function useManuscriptEvents({
    spaceUuid,
    manuscriptId,
    enabled = true,
    isOTConnected = true,
    requiresOTConnection = false,
    onManuscriptChanged,
}) {
    const wsRef = useRef(null);
    const onManuscriptChangedRef = useRef(onManuscriptChanged);
    const [isEventConnected, setIsEventConnected] = useState(false);

    useEffect(() => {
        onManuscriptChangedRef.current = onManuscriptChanged;
    }, [onManuscriptChanged]);

    const connectionReady = useMemo(() => enabled && !!spaceUuid && !!manuscriptId, [enabled, manuscriptId, spaceUuid]);

    const handleMessage = useCallback((event) => {
        const data = JSON.parse(event.data || "{}");
        if (data?.type !== "manuscript_changed") {
            return;
        }
        onManuscriptChangedRef.current?.(data.payload || {});
    }, []);

    useEffect(() => {
        if (!connectionReady) {
            setIsEventConnected(false);
            return undefined;
        }

        const socket = new WebSocket(wsManuscriptEventUrl(spaceUuid, manuscriptId));
        wsRef.current = socket;

        socket.onopen = () => {
            setIsEventConnected(true);
        };
        socket.onmessage = handleMessage;
        socket.onerror = () => {
            setIsEventConnected(false);
        };
        socket.onclose = () => {
            if (wsRef.current === socket) {
                wsRef.current = null;
            }
            setIsEventConnected(false);
        };

        return () => {
            setIsEventConnected(false);
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        };
    }, [connectionReady, handleMessage, manuscriptId, spaceUuid]);

    return {
        isConnected: isEventConnected && (!requiresOTConnection || Boolean(isOTConnected)),
        isEventConnected,
    };
}

export default useManuscriptEvents;
